import React, { useState, useEffect, useRef, useCallback } from "react";
import InputFieldwithSearch from "../inputfields/InputFieldwithSearch";
import DropdownItem from "./DropdownItem";
import { AnimatePresence, motion } from "framer-motion";
import { getFirefoxCssFix } from "../commonfunctions/getFirefoxCssFix";

interface SearchWithDropdownProps {
  options?: string[] | Record<string, any>[];
  onSelect?: (value?: Record<string, any> | string, action?: boolean) => void;
  handleSearchValue?: (value: string, page_number: number) => void;
  id?: string;
  noSearch?: boolean;
  className?: string;
  style?: React.CSSProperties;
  isInfiniteDropdown?: boolean;
  fetchMoreData?: (value: string) => Promise<any>;
  testId?: string;
  dropdownTestId?: string;
  handelDynamicValue?: (value: Record<string, any>) => string;
  dropdownTopClass?: string;
  dynamicDropdown?: boolean;
}

const SearchWithDropdown: React.FC<SearchWithDropdownProps> = ({
  options,
  onSelect,
  handleSearchValue = () => null,
  handelDynamicValue = () => "",
  id,
  dropdownTopClass,
  noSearch,
  className,
  style,
  isInfiniteDropdown = false,
  fetchMoreData,
  testId,
  dropdownTestId,
  dynamicDropdown
}) => {
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loading, setLoading] = useState(dynamicDropdown ? dynamicDropdown : false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleFilterOptions = useCallback(
    (shouldShowLoader = false) => {
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);

      if (dynamicDropdown && shouldShowLoader) setLoading(true);
        filterTimeoutRef.current = setTimeout(() => {
          setFilteredOptions(
            options?.filter((option) =>
              typeof option === "string"
                ? option.toLowerCase().includes(searchTerm.toLowerCase())
                : handelDynamicValue(option).toLowerCase().includes(searchTerm.toLowerCase())
            )
          );
        }, 300);

      loadingTimeoutRef.current = setTimeout(() => {
         setLoading(false);
      }, 600);
    },
    [searchTerm, options, dynamicDropdown, handelDynamicValue]
  );
  useEffect(() => {
    handleFilterOptions(false); // Don't show loader on mount
  }, [handleFilterOptions]);
  useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, []);

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim());
    handleFilterOptions(true); // Show loader on search
    handleSearchValue(value.trim(), 1);
    setHasMoreData(true);
  };

  const handleScroll = async () => {
    if (dropdownRef.current && isInfiniteDropdown && fetchMoreData && hasMoreData) {
      const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        let response = await fetchMoreData(searchTerm ? searchTerm : "");
        if (response?.data?.total_count === 0) {
          setHasMoreData(false);
        }
      }
    }
  };

  return (
    <AnimatePresence>
      <div
        style={style}
        className={`${className} ${dropdownTopClass ? dropdownTopClass : 'top-[85px]'} w-80 rounded-lg border border-P_light_active p-3.5 absolute bg-white z-40`}
      >
        {!noSearch && (
          <InputFieldwithSearch
            className={className}
            handleSearchValue={handleSearch}
            testId={`${id}_search`}
          />
        )}
        <div
          ref={dropdownRef}
          onScroll={handleScroll}
          className={`overflow-y-scroll z-[50] overflow-x-hidden ${getFirefoxCssFix(
            "scroll"
          )} scroll-auto max-h-[240px] p-1 ${noSearch ? "" : "mt-2"}`}
        >
          {(!noSearch && loading) ? (
            <motion.div
              className="space-y-3 animate-pulse"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {[...Array(5)].map((_, index) => (
                <div key={index} className="h-6 w-full bg-gray-400 rounded-md overflow-hidden">
                  <div className="h-full bg-gray-300 rounded-lg animate-pulse"></div>
                </div>
              ))}
            </motion.div>
          ) : filteredOptions?.length === 0 ? (
            <p className="text-body2 text-secondary_text pt-2.5">No data found.</p>
          ) : (
            filteredOptions?.map((option, index) => {
              const optionText =
                typeof option === "string" ? option : handelDynamicValue(option);

              return (
                <DropdownItem
                  key={index}
                  id={`${id}`}
                  dropdownTestId={dropdownTestId}
                  option={optionText}
                  onSelect={() => onSelect(option, false)}
                  index={index}
                />
              );
            })
          )}
        </div>
      </div>
    </AnimatePresence>
  );
};

export default SearchWithDropdown;
